import { ANALYTICS_EVENT, recordEvent } from "../../../hooks/useAnalytics";
import type { DashboardInstance } from "../../../pages/dashboard/mock";
import styles from "./IdentityBar.module.css";

type IdentityBarProps = {
  readonly instance: DashboardInstance;
  // Provided only when the caller may share this instance; renders a quiet
  // inline "Share" affordance next to the tier.
  readonly onShare?: () => void;
};

const ShareIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
    <circle cx="9" cy="7" r="3" />
    <path d="M22 19v-1a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const IdentityBar = ({
  instance,
  onShare,
}: IdentityBarProps): JSX.Element => {
  const handleOpen = (): void => {
    void recordEvent({
      type: ANALYTICS_EVENT.openInstance,
      subject: instance.id,
    });
    window.open(
      `/claw-launch/${encodeURIComponent(instance.id)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const isProvisioning = instance.status === "provisioning";
  const isLocked = instance.locked === true;
  const ctaDisabled = isProvisioning || isLocked;
  const ctaLabel = isLocked
    ? "Getting it ready…"
    : isProvisioning
      ? "Provisioning…"
      : "Open agent";
  const ctaTitle = isLocked
    ? "We're finishing setup for your instance"
    : isProvisioning
      ? "Instance is still provisioning — usually takes a few minutes"
      : `Open ${instance.name} in a new tab`;
  const metaLabel = isLocked ? "in setup" : isProvisioning ? "provisioning" : null;

  return (
    <header className={styles.bar}>
      <div className={styles.left}>
        <span className={styles.eyebrow}>// YOUR INSTANCE</span>
        <h1 className={styles.name}>{instance.name}</h1>
        <div className={styles.metaRow}>
          <span className={styles.planTag}>{instance.tier}</span>
          {metaLabel !== null ? (
            <>
              <span className={styles.metaDivider} aria-hidden="true">·</span>
              <span className={styles.uptimeTag}>{metaLabel}</span>
            </>
          ) : null}
          {onShare !== undefined ? (
            <>
              <span className={styles.metaDivider} aria-hidden="true">·</span>
              <button
                type="button"
                className={styles.shareInline}
                onClick={onShare}
                data-testid="instance-share-button"
              >
                <ShareIcon />
                Share
              </button>
            </>
          ) : null}
        </div>
      </div>
      <div className={styles.right}>
        <button
          type="button"
          className={styles.cta}
          onClick={handleOpen}
          disabled={ctaDisabled}
          title={ctaTitle}
        >
          <span>{ctaLabel}</span>
          {ctaDisabled ? null : (
            <span className={styles.ctaArrow} aria-hidden="true">
              →
            </span>
          )}
        </button>
      </div>
    </header>
  );
};
